import React, { useEffect, useMemo, useState } from 'react'

import { SxProps } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import InputAdornment from 'components_new/atoms/InputAdornment'
import TextField from 'components_new/atoms/TextField'
import Tooltip from 'components_new/atoms/Tooltip'

import IconTip from 'components_new/molecules/IconTip'
import SectionHeader from 'components_new/molecules/SectionHeader'
import ToggleButton from 'components_new/molecules/ToggleButton'

import AutoRefresh, { TAutoRefreshMode } from './AutoRefresh'
import Dimensions, { TDimensions } from './Dimensions'
import Features from './Features'
import Filter from './Filter'
import Other from './Other'
import ResponsiveMode from './ResponsiveMode'

import { useTranslation } from 'language/translator'
import { useSnackbarActions } from 'redux/actions/Snackbar'
import { ParsedDashboardFilterOptions } from 'types/GlobalDashboardFilter'
import { SnackbarMessage } from 'types/GlobalSnackbar'

export type TEmbedFeature =
  | 'filter'
  | 'responsive_mode'
  | 'user_filter'
  | 'image_export'
  | 'hide_footer'
  | 'auto_refresh'

type TOutputMode = 'iframe' | 'link'

interface PreviewEmbeddedContentProps {
  availableFeatures: TEmbedFeature[]
  filterOptions?: Map<string, ParsedDashboardFilterOptions>
  helperText?: React.ReactNode
  link: string
  showPreview?: boolean
  sx?: SxProps
  title: string
}

const PreviewEmbeddedContent = (props: PreviewEmbeddedContentProps) => {
  const {
    availableFeatures,
    filterOptions,
    helperText,
    link,
    showPreview = true,
    sx,
    title
  } = props

  const { t } = useTranslation()
  const { toggleSnackbarOpen } = useSnackbarActions()

  const [outputMode, setOutputMode] = useState<TOutputMode>('iframe')
  const [dimensions, setDimensions] = useState<TDimensions>({
    width: '100%',
    height: '100%'
  })
  const [filter, setFilter] = useState<{ [key: string]: string[] }>({})
  const [mobile, setMobile] = useState<boolean>(false)
  const [breakpoint, setBreakpoint] = useState<string | null>(null)
  const [userFilter, setUserFilter] = useState<boolean>(false)
  const [imageExport, setImageExport] = useState<boolean>(false)
  const [hideFooter, setHideFooter] = useState<boolean>(false)
  const [refreshInterval, setRefreshInterval] = useState<number | null>(null)
  const [refreshAt, setRefreshAt] = useState<string | null>(null)
  const [refreshMode, setRefreshMode] = useState<TAutoRefreshMode>('clock')

  useEffect(() => {
    setOutputMode('iframe')
    setDimensions({ width: '100%', height: '100%' })
    setFilter({})
    setMobile(false)
    setBreakpoint(null)
    setUserFilter(false)
    setImageExport(false)
    setHideFooter(false)
    setRefreshInterval(null)
    setRefreshAt(null)
    setRefreshMode('clock')
  }, [link])

  const outputLink = useMemo(() => {
    if (!link) return ''

    const url = new URL(link)

    if (mobile) {
      url.searchParams.set('responsive_mode', 'mobile')
      if (breakpoint) {
        url.searchParams.set('mobile_breakpoint', breakpoint)
      }
    }

    if (userFilter) {
      url.searchParams.set('filter', 'true')
    }

    if (imageExport) {
      url.searchParams.set('export', 'true')
    }

    if (hideFooter) {
      url.searchParams.set('hide_footer', 'true')
    }

    if (refreshInterval) {
      url.searchParams.set('refresh', String(refreshInterval))
    }

    if (refreshAt) {
      url.searchParams.set('refresh_at', refreshAt)
    }

    for (const [key, values] of Object.entries(filter)) {
      values.forEach((value) => {
        url.searchParams.append(`filter[${key}][eq]`, value)
      })
    }

    return url.toString()
  }, [
    breakpoint,
    filter,
    hideFooter,
    imageExport,
    link,
    mobile,
    refreshAt,
    refreshInterval,
    userFilter
  ])

  const iframeTitle = title.replace(/"/g, '&quot;')
  const outputIframe = `<iframe width="${dimensions.width}" height="${dimensions.height}" src="${outputLink}" title="${iframeTitle}" frameborder="0"></iframe>`
  const previewIframe = `<iframe width="100%" height="100%" src="${outputLink}" title="${iframeTitle}" frameborder="0"></iframe>`

  const output = useMemo(() => {
    if (outputMode === 'iframe') {
      return outputIframe
    }

    return outputLink
  }, [outputMode, outputLink, outputIframe])

  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(output)
    toggleSnackbarOpen(SnackbarMessage.CopiedToClipboard)
  }

  const hasFeature = (feature: TEmbedFeature) =>
    availableFeatures.includes(feature)

  return (
    <Box sx={sx}>
      {link && showPreview ? (
        <Box
          sx={{
            p: 2,
            mb: 2,
            bgcolor: 'action.hover',
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              width: '64vh',
              maxWidth: 640,
              height: '36vh',
              maxHeight: 640,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'background.paper'
            }}
          >
            <div
              dangerouslySetInnerHTML={{ __html: previewIframe }}
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
        </Box>
      ) : null}
      <ToggleButton
        items={[
          {
            title: t('global', 'code'),
            iconName: 'CodeOutlined',
            value: 'iframe'
          },
          {
            title: t('global', 'link'),
            iconName: 'LinkOutlined',
            value: 'link'
          }
        ]}
        value={outputMode}
        onChange={(event, value) => setOutputMode(value)}
        size="small"
      />
      <TextField
        multiline={outputMode === 'iframe'}
        rows={5}
        value={output}
        fullWidth
        helperText={helperText}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip
                title={t(
                  'dashboards',
                  outputMode === 'iframe'
                    ? 'copy_embed_code'
                    : 'copy_embed_link'
                )}
              >
                <IconButton color="primary" onClick={copyLinkToClipboard}>
                  <Icon name="Copy" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
          readOnly: true
        }}
        sx={{ mt: 1 }}
      />
      <SectionHeader
        sx={{ mb: 2, mt: 4 }}
        title={t('global', 'settings')}
        titleAdornment={
          <IconTip title={t('dashboards', 'embed_settings_temporary_notice')} />
        }
      />
      <Box
        sx={{
          p: 2,
          bgcolor: 'action.hover',
          borderRadius: 1
        }}
      >
        <Collapse in={outputMode === 'iframe'}>
          <Dimensions dimensions={dimensions} setDimensions={setDimensions} />
        </Collapse>
        {hasFeature('responsive_mode') ? (
          <ResponsiveMode
            breakpoint={breakpoint}
            mobile={mobile}
            setBreakpoint={setBreakpoint}
            setMobile={setMobile}
          />
        ) : null}
        {hasFeature('user_filter') || hasFeature('image_export') ? (
          <Features
            imageExport={imageExport}
            setImageExport={setImageExport}
            setUserFilter={setUserFilter}
            userFilter={userFilter}
          />
        ) : null}
        {hasFeature('auto_refresh') ? (
          <AutoRefresh
            mode={refreshMode}
            refreshAt={refreshAt}
            refreshInterval={refreshInterval}
            setMode={setRefreshMode}
            setRefreshAt={setRefreshAt}
            setRefreshInterval={setRefreshInterval}
          />
        ) : null}
        {hasFeature('hide_footer') ? (
          <Other hideFooter={hideFooter} setHideFooter={setHideFooter} />
        ) : null}
        {hasFeature('filter') && filterOptions ? (
          <Filter
            filter={filter}
            filterOptions={filterOptions}
            setFilter={setFilter}
          />
        ) : null}
      </Box>
    </Box>
  )
}

export default PreviewEmbeddedContent
